'use client';

import React, { useEffect, useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import { getDeliverables, getDocuments, downloadDocument } from '@/lib/services/portalService';
import { createClient } from '@/lib/supabase/client';

type DeliverableStatus = 'completed' | 'in-progress' | 'upcoming';

interface Deliverable {
  id: string;
  title: string;
  description: string;
  due_date: string | null;
  status: DeliverableStatus;
  phase: string;
}

interface DeliverableImage {
  id: string;
  file_name: string;
  storage_path: string;
  signedUrl?: string;
}

const filterOptions = [
  { id: 'filter-all', label: 'All', value: 'all' },
  { id: 'filter-upcoming', label: 'Upcoming', value: 'upcoming' },
  { id: 'filter-in-progress', label: 'In Progress', value: 'in-progress' },
  { id: 'filter-completed', label: 'Completed', value: 'completed' },
];

interface DeliverablesPanelProps {
  projectId?: string;
}

export default function DeliverablesPanel({ projectId }: DeliverablesPanelProps) {
  const [filter, setFilter] = useState<string>('all');
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [loading, setLoading] = useState(true);
  const [deliverableImages, setDeliverableImages] = useState<Record<string, DeliverableImage[]>>({});
  const [expandedImages, setExpandedImages] = useState<Record<string, boolean>>({});
  const [lightboxImg, setLightboxImg] = useState<{ url: string; name: string } | null>(null);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getDeliverables(projectId).then((data) => {
      setDeliverables(data || []);
      setLoading(false);
    });

    // Real-time subscription
    const supabase = createClient();
    const channel = supabase
      .channel(`deliverables_${projectId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deliverables', filter: `project_id=eq.${projectId}` }, (payload) => {
        if (payload.eventType === 'INSERT') {
          setDeliverables((prev) => [...prev, payload.new as Deliverable]);
        } else if (payload.eventType === 'UPDATE') {
          setDeliverables((prev) => prev.map((d) => d.id === payload.new.id ? payload.new as Deliverable : d));
        } else if (payload.eventType === 'DELETE') {
          setDeliverables((prev) => prev.filter((d) => d.id !== payload.old.id));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [projectId]);

  // Load images for all deliverables once they are fetched
  useEffect(() => {
    if (!projectId || deliverables.length === 0) return;

    const loadImages = async () => {
      const docs = await getDocuments(projectId);
      const grouped: Record<string, DeliverableImage[]> = {};
      for (const d of deliverables) {
        const images = (docs || []).filter(
          (doc: any) => doc.category === `deliverable-image-${d.id}`
        );
        const withUrls = await Promise.all(
          images.map(async (img: any) => {
            const url = await downloadDocument(img.storage_path);
            return { id: img.id, file_name: img.file_name, storage_path: img.storage_path, signedUrl: url || undefined };
          })
        );
        grouped[d.id] = withUrls;
      }
      setDeliverableImages(grouped);
    };

    loadImages();
  }, [projectId, deliverables]);

  const filtered = filter === 'all' ? deliverables : deliverables.filter((d) => d.status === filter);
  const completedCount = deliverables.filter((d) => d.status === 'completed').length;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const toggleImages = (id: string) => {
    setExpandedImages((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <>
      <div className="card">
        <div className="px-5 pt-5 pb-4 border-b border-border">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-base font-bold text-foreground">Project Deliverables</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {completedCount} of {deliverables.length} milestones complete
              </p>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {filterOptions.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setFilter(opt.value)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-150 ${
                    filter === opt.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="divide-y divide-border">
          {loading ? (
            <div className="py-12 text-center">
              <svg className="w-6 h-6 animate-spin text-primary mx-auto mb-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-sm text-muted-foreground">Loading deliverables...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-muted-foreground">No deliverables in this category.</p>
            </div>
          ) : (
            filtered.map((d) => {
              const imgs = deliverableImages[d.id] || [];
              const isExpanded = !!expandedImages[d.id];

              return (
                <div
                  key={d.id}
                  className={`px-5 py-4 transition-colors duration-150 ${
                    d.status === 'completed' ? 'opacity-70' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Status icon */}
                    <div className="flex-shrink-0 mt-0.5">
                      {d.status === 'completed' ? (
                        <div className="w-6 h-6 rounded-full bg-positive flex items-center justify-center">
                          <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        </div>
                      ) : d.status === 'in-progress' ? (
                        <div className="w-6 h-6 rounded-full border-2 border-info bg-info/10 flex items-center justify-center">
                          <span className="w-2 h-2 rounded-full bg-info" />
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-full border-2 border-border bg-card" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <p className={`text-sm font-semibold ${d.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                          {d.title}
                        </p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs font-mono-data text-muted-foreground">{formatDate(d.due_date)}</span>
                          <StatusBadge status={d.status} size="sm" />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{d.description}</p>
                      <span className="inline-flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                        </svg>
                        {d.phase} Phase
                      </span>

                      {/* Images section */}
                      {imgs.length > 0 && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => toggleImages(d.id)}
                            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <svg
                              className={`w-3 h-3 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                            </svg>
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                            </svg>
                            <span className="font-medium text-foreground">{imgs.length} {imgs.length === 1 ? 'image' : 'images'}</span>
                          </button>

                          {isExpanded && (
                            <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                              {imgs.map((img) => (
                                <button
                                  key={img.id}
                                  type="button"
                                  onClick={() => img.signedUrl && setLightboxImg({ url: img.signedUrl, name: img.file_name })}
                                  className="relative rounded-lg overflow-hidden border border-border aspect-video bg-muted hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary/40"
                                  title={img.file_name}
                                >
                                  {img.signedUrl ? (
                                    <img
                                      src={img.signedUrl}
                                      alt={img.file_name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <svg className="w-4 h-4 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909" />
                                      </svg>
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-foreground/80 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <div
            className="relative max-w-4xl w-full max-h-[90vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightboxImg(null)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white transition-colors"
              aria-label="Close image"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={lightboxImg.url}
              alt={lightboxImg.name}
              className="max-w-full max-h-[80vh] rounded-xl object-contain shadow-2xl"
            />
            <p className="mt-3 text-sm text-white/70 truncate max-w-full">{lightboxImg.name}</p>
          </div>
        </div>
      )}
    </>
  );
}